<template>
  <div
    class="text-#333 text-14 h-100vh flex flex-col AIChat-page relative"
    ref="aiChatRef"
  >
    <Navbar @clearHistoryMessage="clearHistoryMessage" />
    <div class="flex-1 overflow-y-hidden relative">
      <div
        v-if="loading"
        class="text-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      >
        <van-loading type="spinner" />
      </div>
      <BackToBottom :target="messageListRef" ref="backToBottomRef" />
      <van-pull-refresh
        class="h-full flex-1 flex flex-col"
        v-model="historyLoading"
        @refresh="onLoadHistory"
        pulling-text=" "
        loading-text=" "
        loosing-text=" "
        :animation-duration="0"
        :head-height="10"
        :disabled="historyLoading === undefined || !isAtTop"
      >
        <ul
          class="h-full flex-1 flex flex-col overflow-y-scroll px-10 overscroll-contain"
          ref="messageListRef"
          style="-webkit-overflow-scrolling: touch"
          @touchstart="checkScrollTop"
          @touchmove="checkScrollTop"
        >
          <template v-for="(item, index) in messageList" :key="index">
            <MessageItem
              :item="item"
              :index="index"
              :message-list="messageList"
              :has-history-message="hasHistoryMessage"
              :send-loading="sendLoading"
              :reply-loading="replyLoading"
              :greeting-word-list="greetingWordList"
              @send-message="sendMessage"
              @regenerate-message="regenerateMessage"
              @refresh-greeting-word="getGreetingWordListMessage"
            >
            </MessageItem>
          </template>

          <li ref="endOfMessageRef"></li>
        </ul>
      </van-pull-refresh>
    </div>
    <FooterButton
      v-if="!loading"
      :disabled="loading || replyLoading || sendLoading || !canRecordAudio"
      @sendMessage="sendMessage"
      @focus="onFocus"
      @audio="handleAudio"
      ref="footerRef"
      :topic-list="recTopicList"
      v-model:message="messageContent"
    />
  </div>
</template>

<script lang="ts" setup>
import { getRecTopic, getSessionId } from "@/api/aiChat";
import { computed, nextTick, onMounted, ref, toRaw } from "vue";
import { useSetting } from "@/composables/use-setting";
import {
  setNavigationBar,
  setStatusBarStyle,
  StatusBarStyle,
} from "@voghion/native-api";
import FooterButton from "./FooterButton/index.vue";
import { handleUploadFile } from "@/api/request";
import {
  readSSEResponse,
  sendMessageRequestWithSessionId,
} from "./readSSEResponse";
import { Message } from "./type";
import { showDialog, showFailToast } from "vant";
import db from "./utils/db";
import BackToBottom from "./BackToBottom.vue";
import MessageItem from "./MessageItem.vue";
import Navbar from "./Navbar.vue";
import { useI18n } from "vue-i18n";
import { useLoadHistory } from "./utils/useLoadHistory";

let key = Date.now();

const { t } = useI18n();
const loading = ref<boolean>(false);
const sendLoading = ref<boolean>(false);
const replyLoading = ref<boolean>(false);
const sessionId = ref<number>();
const messageList = ref<Message[]>([]);
const messageListRef = ref<HTMLUListElement | null>(null);
const greetingWordList = ref<string[]>([]);
const endOfMessageRef = ref<HTMLDivElement>();
const footerRef = ref();
const { getClientInfo } = useSetting();
const hasHistoryMessage = ref<boolean>(false);
const recTopicList = ref<string[]>([]);
const { onLoadHistory, historyLoading, isAtTop } = useLoadHistory({
  messageListRef,
  messageList,
});

const inputRef = computed(() => footerRef.value?.inputRef);
const messageContent = ref<string>("");
const aiChatRef = ref<HTMLDivElement | null>(null);

const canRecordAudio = ref(true);
const backToBottomRef = ref<InstanceType<typeof BackToBottom>>();

// 检查是否在顶部
const checkScrollTop = () => {
  const listDom = messageListRef.value;

  if (!listDom) return;
  backToBottomRef.value?.checkScroll(); // 由于 BackToBottom 组件外部ref已经绑定了mousemove事件，所以需要在外部这里调用检查
  isAtTop.value = listDom.scrollTop <= 10;
};

const historyLength = ref(0);

const handleAudio = async (audioBlob: Blob, imageList: string) => {
  canRecordAudio.value = false;
  try {
    const audioMessage = pushMessage({
      role: "user",
      type: "text",
      audio: {
        url: "",
      },
      reasonContent: "",
      imageList,
    });
    const name = "ai-customer-" + Date.now() + ".mp3";
    const audioUrl = await handleUploadFile({
      blob: audioBlob,
      type: audioBlob.type,
      name,
      extension: "mp3",
    });
    if (audioUrl?.data) {
      messageList.value[messageList.value.length - 1].audio!.url =
        audioUrl.data;
      const reader = await sendMessageRequest({
        multimediaType: "VOICE",
        multimediaUrl: audioUrl.data,
      });

      if (reader) {
        readSSEResponse({
          reader,
          type: "VOICE",
          onData: (message) => {
            if (message.reasonContent) {
              audioMessage.reasonContent += message.reasonContent || "";
            }
          },
          onError: () => {
            const index = messageList.value.findIndex(
              (item) => item.id === audioMessage.id,
            );
            if (index !== -1) {
              messageList.value.splice(index, 1);
            }
            showFailToast({
              message: t("message.ai.recordInvisible"),
              className: "w-80%!",
            });
            canRecordAudio.value = true;
          },
          onDone: () => {
            pushMessage(audioMessage);
            sendMessage({
              ...audioMessage,
              imageList,
            });
            canRecordAudio.value = true;
          },
        });
      }
    } else {
      showFailToast({
        message: t("message.ai.failedResolveAudio"),
      });
    }
  } catch (error) {
    canRecordAudio.value = true;
    console.error(error);
  }
};

const pushMessage = (message: Partial<Message>) => {
  if (message.id) {
    const index = messageList.value.findIndex((item) => item.id === message.id);
    if (index !== -1) {
      messageList.value[index] = { ...messageList.value[index], ...message };
      scrollMessageIntoBottom();
      return messageList.value[index];
    }
  }
  key++;
  const newMessage = {
    reasonContent: "",
    thoughtContent: "",
    date: Date.now(),
    id: key,
    ...message,
  } as Message;
  messageList.value.push(newMessage);
  scrollMessageIntoBottom();
  if (newMessage.imageList) {
    const img = new Image();
    img.src = newMessage.imageList;
    img.onload = () => {
      scrollMessageIntoBottom();
    };
  }
  return newMessage;
};

const getGreetingMessage = async () => {
  try {
    const reader = await sendMessageRequest({ multimediaType: "GREETING" });

    const index = messageList.value.length;
    if (reader) {
      pushMessage({ role: "assistant", type: "greeting" });

      readSSEResponse({
        reader,
        type: "GREETING",
        onData: (message) => {
          if (message.reasonContent) {
            messageList.value[index].reasonContent +=
              message.reasonContent || "";
          }
        },
        onDone: () => {
          // scrollMessageIntoBottom();
        },
      });
    }
  } catch (error) {
    console.error(error);
  }
};

const sendMessageRequest = async (params: {
  multimediaType: string;
  content?: string;
  multimediaUrl?: string;
}) => {
  if (!sessionId.value) return;
  return sendMessageRequestWithSessionId(params, sessionId.value);
};

const getGreetingWordListMessage = async () => {
  greetingWordList.value = ["", "", "", "", ""];
  const reader = await sendMessageRequest({
    multimediaType: "GREETING_WORLD_LIST",
  });

  if (reader) {
    readSSEResponse({
      reader,
      type: "GREETING_WORLD_LIST",
      onData: (message) => {
        if (message.reasonContent) {
          greetingWordList.value = message.reasonContent.slice(0, 8);
        }
      },
      onDone: () => {},
    });
  }
};

const sendMessage = async (message: Partial<Message>) => {
  const { reasonContent: content = "", imageList } = message;
  if (!content) {
    return inputRef.value.focus();
  }
  scrollMessageIntoBottom();
  recTopicList.value = [];
  let lastMessageId: number | null = null;
  try {
    let fullUserMessage: Message;
    if (message.id) {
      fullUserMessage = message as Message;
    } else {
      fullUserMessage = pushMessage({
        role: "user",
        reasonContent: content,
        type: "text",
        imageList,
      });
    }
    cacheMessage(fullUserMessage);
    const fullAiMessage = pushMessage({
      role: "assistant",
      type: "text",
      reasonContent: "",
    });
    lastMessageId = fullAiMessage.id;
    sendLoading.value = true;
    const reader = await sendMessageRequest({
      multimediaType: imageList ? "IMAGE" : "TEXT",
      content,
      multimediaUrl: imageList,
    });
    sendLoading.value = false;

    if (reader) {
      messageContent.value = "";
      replyLoading.value = true;
      readSSEResponse({
        reader,
        type: "TEXT",
        onData: (message) => {
          if (message.completeStatus === 1 || message.completeStatus === 2) {
            fullAiMessage.reasonContent += message.reasonContent || "";
            if (message.thoughtContent) {
              fullAiMessage.thoughtContent += message.thoughtContent || "";
            }
          } else if (message.completeStatus === 4) {
            sessionId.value = undefined;
            messageContent.value = content;
          }
          fullAiMessage.requestId = message.requestId;
          fullAiMessage.sessionId = message.sessionId;
          fullAiMessage.stageId = message.stageId;
          pushMessage(fullAiMessage);
          cacheMessage(fullAiMessage);
        },
        onGoodsList: async (reasonContent) => {
          const { itemList, requestId, sessionId, stageId } = reasonContent;
          const newMessage = pushMessage({
            id: fullAiMessage.id,
            goodsList: itemList,
            requestId,
            sessionId,
            stageId,
          });
          cacheMessage(newMessage);
          const goodsListId = reasonContent.itemList.map(
            (item: any) => item.itemId,
          );
          if (goodsListId.length) {
            const { countryCode, language } = getClientInfo();
            const res = await getRecTopic({
              sessionId: reasonContent.sessionId,
              requestId: reasonContent.requestId,
              deviceCode: reasonContent.deviceCode,
              countryCode,
              language,
              itemIds: goodsListId,
            });
            const json = await res.json();
            if (json?.topicList) {
              recTopicList.value = json.topicList;
              scrollMessageIntoBottom();
            }
          }
        },
        onDone: () => {
          replyLoading.value = false;
        },
      });
    }
  } catch (error) {
    console.error(error);
    if (lastMessageId) {
      const newMessage = pushMessage({
        id: lastMessageId,
        reasonContent: t("message.ai.serverBusy"),
      });
      cacheMessage(newMessage);
    }
  } finally {
    replyLoading.value = false;
    sendLoading.value = false;
    scrollMessageIntoBottom();
  }
};

const regenerateMessage = async (id: number, resolve: () => void) => {
  const index = messageList.value.length - 1;
  const prevMessage = messageList.value[index - 1];
  await db.messageList.delete(id);
  messageList.value.splice(index, 1);
  await sendMessage(prevMessage);
  resolve();
};

const clearHistoryMessage = async () => {
  showDialog({
    className: "clear-history-dialog",
    title: t("message.ai.clearHistory"),
    showCancelButton: true,
    cancelButtonText: t("message.ai.cancel"),
    confirmButtonText: t("message.ai.clear"),
  }).then(async () => {
    sessionId.value = undefined;
    await db.messageList.clear();
    hasHistoryMessage.value = false;
    messageList.value = [];
    hasHistoryMessage.value = false;
    recTopicList.value = [];
    initData();
  });
};

const cacheMessage = async (refMessage: Message) => {
  const message = { ...toRaw(refMessage) };
  if (message.goodsList) {
    message.goodsList = JSON.stringify(message.goodsList) as any;
  }
  console.log("cacheMessage", message);
  await db.messageList.put(message);
};

const scrollMessageIntoBottom = async () => {
  await nextTick();
  const endOfMessageDom = endOfMessageRef.value;
  if (endOfMessageDom) {
    endOfMessageDom.scrollIntoView();
  }
};

const onFocus = async (isFocus: boolean) => {
  if (isFocus) {
    setTimeout(() => {
      scrollMessageIntoBottom();
    }, 300);
  }
};

const initSessionId = async () => {
  const deviceCode = getClientInfo().deviceCode;
  const res = await getSessionId({ deviceCode });
  const json = await res.json();
  if (json?.sessionId) {
    sessionId.value = json.sessionId;
  }
};

const initData = async () => {
  loading.value = true;
  await initSessionId();
  await getGreetingMessage();
  loading.value = false;
  await getGreetingWordListMessage();
  scrollMessageIntoBottom();
  historyLength.value = await db.messageList.count();
};
onMounted(async () => {
  setNavigationBar({
    hide: true,
  });
  setStatusBarStyle({
    style: StatusBarStyle.Black,
  });
  document.title = t("message.ai.title");

  initData();
});
</script>
<style lang="scss">
.clear-history-dialog {
  * {
    user-select: none;
  }
  .van-dialog__header {
    padding-top: 32px !important;
    padding-left: 20px;
    padding-right: 20px;
    font-size: 18px;
  }
  .van-dialog__footer {
    padding: 10px 17px 22px 17px;
    &::after {
      display: none;
    }
    .van-button {
      border-radius: 8px;
      font-size: 16px;
    }
  }
  .van-dialog__cancel {
    background: #eeeeee;
    margin-right: 14px;
  }
  .van-dialog__confirm {
    background: linear-gradient(90deg, #255dfc 0%, #283efa 100%);
    color: #fff;
  }
}
</style>
<style lang="scss" scoped>
html,
body,
#app,
.van-config-provider {
  height: 100%;
}
</style>
<style lang="scss">
.AIChat-page {
  * {
    box-sizing: border-box;
  }
}

.input-field {
  .van-field__button {
    display: flex;
    align-items: center;
  }
}
.markdown-message-content {
  p {
    margin: 10px 0;
  }
}
</style>
